import { useEffect, useMemo, useRef } from 'react';

import { Identifier, RaRecord } from 'react-admin';

type Props = {
  allKnownChoices: RaRecord[];
  optionValue: string;
  selectedIds: Identifier[];
};

export const useOptionsCache = ({
  allKnownChoices,
  optionValue,
  selectedIds,
}: Props): RaRecord[] => {
  const cacheRef = useRef<Map<Identifier, RaRecord>>(new Map());

  useEffect(() => {
    allKnownChoices.forEach((opt) => {
      const id = opt?.[optionValue];
      if (!id) {
        return;
      }
      const existing = cacheRef.current.get(id);
      cacheRef.current.set(id, existing ? { ...existing, ...opt } : opt);
    });
  }, [allKnownChoices, optionValue]);

  useEffect(() => {
    if (!Array.isArray(selectedIds)) {
      cacheRef.current.clear();
      return;
    }
    const selectedIdSet = new Set(selectedIds);
    cacheRef.current.forEach((_, id) => {
      if (!selectedIdSet.has(id)) {
        cacheRef.current.delete(id);
      }
    });
  }, [selectedIds]);

  return useMemo<RaRecord[]>(() => {
    if (!Array.isArray(selectedIds)) {
      return [];
    }

    const cache = cacheRef.current;
    return Array.from(new Set(selectedIds))
      .map((id) => cache.get(id) ?? allKnownChoices.find((opt) => opt?.[optionValue] === id))
      .filter((opt): opt is RaRecord => Boolean(opt));
  }, [allKnownChoices, optionValue, selectedIds]);
};
